import React from 'react';

import noop from 'lodash/noop';

import { useDynamicIcon } from '@lumapps/assets/hooks/useDynamicIcon';
import { cache, CACHE_TYPE } from '@lumapps/cache';
import { classnames } from '@lumapps/classnames';
import { useIsInView } from '@lumapps/lumx-hooks/hooks/useIsInView';
import { BO_SIDE_NAV_CACHE_SCROLL_ID } from '@lumapps/lumx-spotlight/constants';
import { useSpotlightActions } from '@lumapps/lumx-spotlight/hooks/useSpotlightActions';
import type { NavigationPage } from '@lumapps/lumx-spotlight/types';
import {
    SideNavigationItem as NavigationItem,
    SideNavigationItemProps as NavigationProps,
    Emphasis,
} from '@lumapps/lumx/react';
import { Route, useRouteMatching, useLinkProps, useLocation } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { scrollIntoView } from '@lumapps/utils/browser/scrollIntoView';

import { useSideBarNavigationDataAttributes } from '../../../hooks/useSideBarNavigationDataAttributes';
import { SideNavigationMenu } from './SideNavigationMenu';

import './index.scss';

export interface SideNavigationItemProps extends Omit<NavigationProps, 'toggleButtonProps'> {
    label: string;
    className?: string;
    dynamicIcon?: string;
    pathMatchForHighlighting?: string;
    route?: Route;
    id?: string;
    openInNewTab?: boolean;
    /**
     * Each route provided in excludedRoutes array will prevent item to be selected when matching
     * Useful when 2 sub-menu items have similar routes but one is too "weak". E.g:
     * - item "Campaigns > All campaigns" = /admin/campaigns
     * - item "Campaigns > Settings" = /admin/campaigns/settings
     * Providing settings route to excludedRoutes of "All campaigns" item prevents isSelected state when navigating to settings route
     */
    excludedRoutes?: Route[];
    sectionLabel?: string;
    hasParent?: boolean;
    // Reference here the keys or keywords you want the search to index, so the user can easily find the relevant page
    pageKeys?: string[];
}

export const CLASSNAME = 'side-navigation-item';
/**
 * Side Navigation Item that can display a single item or a section with multiple links
 * /!\ This component needs to be wrapped in either SpotlightProvider or the back-office SideNavigation component to work properly
 * (because it uses useSpotlightActions hook internally to index all available nav links)
 *
 * @family Back office
 * @param SideNavigationItemProps
 * @returns SideNavigationItem
 */
const SideNavigationItem: React.FC<SideNavigationItemProps> = ({
    className,
    children,
    isOpen = false,
    route,
    dynamicIcon = '',
    linkProps = {},
    icon,
    openInNewTab = false,
    pathMatchForHighlighting,
    excludedRoutes = [],
    sectionLabel,
    hasParent = false,
    pageKeys,
    ...otherProps
}) => {
    const { translateKey } = useTranslate();
    const { pathname } = useLocation();
    const isRouteMatching = useRouteMatching(route, pathMatchForHighlighting || `${route?.path}/*`);
    const isCurrentRoute = excludedRoutes.map((route) => route.path).includes(pathname) ? false : isRouteMatching;

    const { iconPath } = useDynamicIcon({ icon: dynamicIcon });
    const dataAttributes = useSideBarNavigationDataAttributes({ route });
    const { onClick, ...routerLinkProps } = useLinkProps({ route, target: linkProps.target, openInNewTab });
    const itemRef = React.useRef<HTMLLIElement>(null);
    const { ref: inViewRef, inView: isItemInView } = useIsInView();

    const classname = classnames(CLASSNAME, className);
    const iconToUse = dynamicIcon ? iconPath : icon || undefined;

    // Register this item with navigation collection for Spotlight search
    // Only register standalone items (those NOT inside a SideNavigationMenu)
    const { registerPage, unregisterPage } = useSpotlightActions();

    React.useEffect(() => {
        // SideNavigationMenu already registers its children via extractRoutes
        // Only register if this is a standalone item (no hasParent prop) and has a route
        if (!hasParent && route && otherProps.label) {
            const navigationItem: NavigationPage = {
                id: otherProps.id || `${otherProps.label}:${route.path}`,
                label: otherProps.label,
                route,
                icon: iconToUse,
                sectionLabel,
                pageKeys,
            };

            registerPage(navigationItem);

            return () => {
                unregisterPage(navigationItem.id);
            };
        }

        return noop;
    }, [
        hasParent,
        route,
        otherProps.label,
        otherProps.id,
        iconToUse,
        sectionLabel,
        registerPage,
        unregisterPage,
        pageKeys,
    ]);

    /**
     * Effect in order to determine whether we should scroll the side navigation item
     * into view. We only want to execute this once, since after the user has navigated (scroll or clicked on another item)
     * we no longer want to scroll anything into view.
     *
     * We use in memory cache in order to determine whether the side nav was scrolled or not.
     */
    React.useEffect(() => {
        const wasScrolled = cache.retrieve(BO_SIDE_NAV_CACHE_SCROLL_ID, CACHE_TYPE.MEMORY);

        inViewRef(itemRef.current);

        if (isCurrentRoute && !isItemInView && !wasScrolled && itemRef.current) {
            scrollIntoView(itemRef.current, { scrollMode: 'if-needed' });

            cache.store(BO_SIDE_NAV_CACHE_SCROLL_ID, true, CACHE_TYPE.MEMORY);
        }
    }, [inViewRef, isCurrentRoute, isItemInView]);
    /**
     * We check whether there are more navigation items passed in as children in order to determine
     * whether we need to add the open/close behaviour or not. If the prop is defined, it means that
     * we need to display more links on this section and we need to allow the user to open and close
     * the section to see the other links, therefore rendering the SideNavigationMenu component
     */
    const hasChildren = Boolean(children);

    // If the item in only a container, do not display it when there are no children
    if (!hasChildren && !routerLinkProps.href) {
        return null;
    }

    if (hasChildren) {
        return (
            <SideNavigationMenu
                className={className}
                baseClassName={CLASSNAME}
                isOpen={isOpen}
                icon={iconToUse}
                sectionLabel={sectionLabel}
                {...otherProps}
            >
                {children}
            </SideNavigationMenu>
        );
    }

    return (
        <NavigationItem
            {...otherProps}
            ref={itemRef}
            className={classname}
            emphasis={Emphasis.medium}
            isSelected={isCurrentRoute}
            linkProps={{
                ...linkProps,
                ...routerLinkProps,
            }}
            icon={iconToUse}
            toggleButtonProps={{ label: translateKey(GLOBAL.TOGGLE) }}
            {...dataAttributes}
            onClick={onClick}
        />
    );
};

SideNavigationItem.displayName = 'SideNavigationItem';

export { SideNavigationItem };
